import { addressResolutionResponseSchema, type AddressResolutionResponse } from '@erp/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiFetch, type ApiError } from '@/lib/api-client';

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [delayMs, value]);
  return debounced;
}

export function useAddressResolution(
  countryCode: string | null | undefined,
  postalCode: string | null | undefined,
): UseQueryResult<AddressResolutionResponse, ApiError> {
  const key = `${countryCode?.trim().toUpperCase() ?? ''}:${postalCode?.trim() ?? ''}`;
  const debouncedKey = useDebouncedValue(key, 350);
  const [debouncedCountry = '', debouncedPostal = ''] = key.split(':');
  const enabled =
    key === debouncedKey &&
    (debouncedCountry === 'DE' || debouncedCountry === 'FR') &&
    /^\d{5}$/.test(debouncedPostal);
  const search = new URLSearchParams({
    countryCode: debouncedCountry,
    postalCode: debouncedPostal,
  });

  return useQuery({
    queryKey: ['address-resolution', key],
    queryFn: ({ signal }) =>
      apiFetch(`/api/v1/address-resolution?${search.toString()}`, addressResolutionResponseSchema, {
        signal,
      }),
    enabled,
    retry: 1,
    staleTime: 24 * 60 * 60 * 1000,
  });
}
